import { deepEqual, deepFreeze, deepMergeCloneObject, getIn, hasOwn } from '@aytm/lib/common/object'
import Subscription from '@aytm/lib/common/subscription'
import DevLog from '../devlog/Module'

import { ModuleID } from '../types'
import loadDict from './loadDict'
import { ILocaleModule, LocaleID, LocaleValue } from './types'

import { DEFAULT_LOCALE_ID, LOCALES, LOCALE_DICT_LOADING, PRELOAD_LOCALES, ensureLocale } from './state'
export { DEFAULT_LOCALE_ID }

const DEBUG_LOCALE = 'development' === process.env.NODE_ENV

let moduleLoaded = false
const LocaleModule: ILocaleModule = {
  get id() {
    return ModuleID.LOCALE
  },
  config({ locales = {}, preload } = {}) {
    for (const localeID in locales) {
      if (hasOwn(locales, localeID)) {
        LocaleModule.setLocale(localeID, locales[localeID], moduleLoaded)
      }
    }
    if (Array.isArray(preload)) {
      if (moduleLoaded) {
        preload.forEach(localeID => {
          void LocaleModule.loadLocaleDict(localeID)
        })
      } else {
        preload.forEach(localeID => {
          PRELOAD_LOCALES.add(localeID)
        })
      }
    }
    return LocaleModule
  },
  async load() {
    if (PRELOAD_LOCALES.size) {
      await Promise.all([...PRELOAD_LOCALES].map(localeID => LocaleModule.loadLocaleDict(localeID)))
      PRELOAD_LOCALES.clear()
    }
    moduleLoaded = true
    return LocaleModule
  },
  get loaded() {
    return moduleLoaded
  },
  hasLoadedLocale(localeID, orLoading = false) {
    return Boolean(LOCALES[localeID] && LOCALES[localeID].loaded) || (orLoading && LOCALE_DICT_LOADING.has(localeID))
  },
  hasUpdatedFromServerLocale(localeID) {
    return Boolean(LOCALES[localeID] && LOCALES[localeID].updatedFromServer)
  },
  async loadLocaleDict(localeID) {
    if (!LocaleModule.hasLoadedLocale(localeID) && !LOCALE_DICT_LOADING.has(localeID)) {
      if (DEBUG_LOCALE) {
        DevLog._moduleLog(LocaleModule, 'loadLocaleDict', localeID)
      }
      try {
        LOCALE_DICT_LOADING.add(localeID)
        const { dict, rtl, textDir } = await loadDict(localeID)
        LOCALE_DICT_LOADING.delete(localeID)
        LocaleModule.setLocale(localeID, { dict, options: { rtl: Boolean(rtl), textDir }, setLoaded: true })
      } catch (error) {
        DevLog.moduleError(LocaleModule, error, 'Failed to load locale dict')
      }
    }
    return LocaleModule
  },
  setLocale(localeID, { dict, options, setLoaded = false, setUpdatedFromServer = false } = {}, notify = true) {
    if (DEBUG_LOCALE) {
      DevLog._moduleLog(LocaleModule, 'setLocale', localeID, { dict, options, setLoaded, setUpdatedFromServer })
    }
    const locale = ensureLocale(localeID)
    const localeBackupDict = locale.dict
    const localeBackupOptions = locale.options
    if (dict) {
      locale.dict = deepFreeze(deepMergeCloneObject(locale.dict, dict))
    }
    if (options) {
      locale.options = deepFreeze(deepMergeCloneObject(locale.options, options))
    }
    if (setLoaded) {
      locale.loaded = true
    }
    if (setUpdatedFromServer) {
      locale.updatedFromServer = true
    }
    if (
      notify &&
      locale.subscription &&
      ((dict && !deepEqual(localeBackupDict, locale.dict)) ||
        (options && !deepEqual(localeBackupOptions, locale.options)))
    ) {
      locale.subscription.update(localeID)
    }
    return LocaleModule
  },
  options(localeID) {
    return ensureLocale(localeID).options
  },
  t(localeID = DEFAULT_LOCALE_ID, path, fallbackValue) {
    const locale = ensureLocale(localeID)
    return getIn<LocaleValue>(
      locale.dict,
      path,
      'undefined' === typeof fallbackValue
        ? localeID === DEFAULT_LOCALE_ID
          ? DEFAULT_FALLBACK_VALUE
          : DEFAULT_FALLBACK_VALUE_WITH_DEFAULT_LOCALE_LOOKUP
        : fallbackValue
    )
  },
  onChange(localeID = DEFAULT_LOCALE_ID, listener) {
    if (!LOCALES[localeID]?.subscription) {
      ensureLocale(localeID).subscription = Subscription<LocaleID, ILocaleModule>()
    }
    return LOCALES[localeID].subscription.listen(listener, LocaleModule)
  }
}

export default LocaleModule

export function DEFAULT_FALLBACK_VALUE(_target: object, path: Array<string | number | symbol>) {
  DevLog._moduleLog(LocaleModule, 'Missing locale value', path)
  return 'development' === process.env.NODE_ENV ? `!${path.join('.')}!` : ''
}

export function DEFAULT_FALLBACK_VALUE_WITH_DEFAULT_LOCALE_LOOKUP(
  _target: object,
  path: Array<string | number | symbol>
) {
  return getIn<LocaleValue>(LOCALES[DEFAULT_LOCALE_ID].dict, path, DEFAULT_FALLBACK_VALUE)
}
